export interface FaqItem {
  question: string;
  answer: string;
}

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  tags?: string[];
  // Optional FAQ — rendered both as a visible section and as FAQPage JSON-LD,
  // built from this single source so the two always match (required by Google).
  faq?: FaqItem[];
}

// Add new posts at the TOP of the array — the first entry is always shown as featured on the homepage.
const blogPosts: BlogPost[] = [
  {
    slug: 'falske-mtg-kort',
    title: 'Slik avslører du falske Magic-kort – 9 tester du kan gjøre hjemme',
    excerpt:
      'Falske MTG-kort blir stadig bedre. Her er testene som avslører dem – fra lystesten til green dot-testen – og hvorfor vi sjekker hver eneste samling vi kjøper for forfalskninger.',
    date: '2026-10-29',
    tags: ['MTG', 'falske kort', 'forfalskning', 'ekthet', 'selge'],
    content: `
<p>
  Jo mer et Magic-kort er verdt, jo større er sjansen for at noen har forsøkt å kopiere det.
  Forfalskningene har blitt langt bedre de siste årene, og mange av dem lurer folk som har spilt i
  årevis. Den gode nyheten er at selv de beste kopiene nesten alltid feiler på minst én av testene
  under. Du trenger ikke dyrt utstyr – et ekte kort å sammenligne med, en mobillykt og helst en
  lupe kommer du langt med.
</p>

<h2>Hvilke kort blir forfalsket?</h2>
<p>
  Forfalskere går etter penger, så det er de dyre kortene som er utsatt:
</p>
<ul>
  <li><strong>Reserved List-kort</strong> som dual lands, Moxer og andre kort fra Alpha, Beta,
    Unlimited og Revised. De trykkes aldri på nytt, og prisene er høye.</li>
  <li><strong>Dyre staples</strong> fra Modern, Legacy og Commander – kort som går for flere
    hundre kroner og som mange vil ha.</li>
  <li><strong>Foils og spesialversjoner</strong>, der kjøperen ofte har lite å sammenligne med.</li>
</ul>
<p>
  Et kort til fem kroner er sjelden verdt å forfalske. Har du et kort som er verdt mye – eller et
  kort som er <em>for billig</em> til å være ekte når du kjøper det – er det verdt å ta testene.
</p>

<h2>1. Sammenlign med et kort du vet er ekte</h2>
<p>
  Den viktigste testen er også den enkleste. Legg det mistenkelige kortet ved siden av et vanlig
  kort fra samme periode – gjerne en billig common du har åpnet selv fra en booster. Se på dem
  side om side i godt lys. Mange forfalskninger avslører seg allerede her, særlig på baksiden.
</p>

<h2>2. Se på baksiden</h2>
<p>
  Baksiden er den samme på nesten alle Magic-kort siden 1993, og den er vanskelig å treffe
  perfekt. Typiske feil på falske kort:
</p>
<ul>
  <li>Det blå er for mørkt, for lyst eller for mettet.</li>
  <li>Den brune rammen har feil nyanse eller er for blek.</li>
  <li>Detaljene i det oransje feltet rundt logoen er uskarpe eller «grøtete».</li>
</ul>

<h2>3. Lystesten</h2>
<p>
  Ekte Magic-kort er laget av to lag papp med et mørkt lag lim imellom. Dette laget slipper
  gjennom svært lite lys. Hold kortet foran mobillykten i et mørkt rom, og gjør det samme med et
  ekte kort. Det ekte kortet slipper gjennom et svakt, dempet lys. Mange falske kort lyser
  tydelig opp – noen så mye at du kan se motivet på andre siden.
</p>
<p>
  Testen er ikke perfekt: de beste forfalskningene har begynt å kopiere det mørke laget. Men
  feiler et kort lystesten, er det nesten helt sikkert falskt.
</p>

<h2>4. Lupetesten – se etter rosettmønsteret</h2>
<p>
  Dette er den mest pålitelige testen du kan gjøre hjemme. Ekte kort er trykket med offsettrykk,
  og ser du på et farget område gjennom en lupe (10x er nok, mange mobilkameraer klarer det med
  zoom), ser du et mønster av små prikker i cyan, magenta, gult og svart som danner små
  «rosetter».
</p>
<p>
  Falske kort er ofte trykket med blekk- eller laserskriver. Under lupen ser du da enten et
  tilfeldig prikkemønster, striper, eller en jevn flate uten struktur. Sammenlign alltid med et
  ekte kort fra samme epoke – trykketeknikken har endret seg litt over årene.
</p>

<p>
  Et godt sted å se er ordet «The» i «The Gathering» på baksiden av kortet. Bildene under viser
  samme område på et ekte og et falskt kort. På det ekte kortet ligger prikkene i et jevnt,
  regelmessig rosettmønster. På det falske er mønsteret mer uryddig og flytende, og prikkene
  ligger ikke i faste rader.
</p>

<div class="figure-pair">
  <figure>
    <img src="/blog-images/rosettmonster-ekte-magic-kort.jpg" alt="Rosettmønsteret i «The» på baksiden av et ekte Magic-kort under lupe" loading="lazy" />
    <figcaption>Ekte kort: jevnt og regelmessig rosettmønster.</figcaption>
  </figure>
  <figure>
    <img src="/blog-images/rosettmonster-falsk-magic-kort.jpg" alt="Rosettmønsteret i «The» på baksiden av et falskt Magic-kort under lupe" loading="lazy" />
    <figcaption>Falskt kort: uryddig mønster uten faste rader.</figcaption>
  </figure>
</div>

<h2>5. Green dot-testen</h2>
<p>
  Green dot-testen er en variant av lupetesten, og en av de mest kjente ekthetstestene i
  Magic-miljøet. Snu kortet og finn den grønne prikken blant de fem fargede manaprikkene på
  baksiden. Se på den lyse delen av den grønne prikken gjennom en lupe – helst 20–30x.
</p>
<p>
  På ekte kort er det grønne bygget opp av et ordnet rosettmønster av blå og gule prikker, og et
  sted i det lyse feltet ser du <strong>fire små røde prikker som danner en «L»</strong>. Dette
  har vært likt på ekte kort helt siden Alpha. Falske kort viser som regel bare tilfeldige
  prikker eller en jevn grønnfarge, og «L»-en mangler eller ser feil ut.
</p>

<div class="figure-pair">
  <figure>
    <img src="/blog-images/green-dot-ekte-magic-kort.jpg" alt="Den grønne prikken på baksiden av et ekte Magic-kort under lupe, med fire små røde prikker i en L" loading="lazy" />
    <figcaption>Ekte kort: de fire røde prikkene i en «L» ligger i det lyse feltet.</figcaption>
  </figure>
  <figure>
    <img src="/blog-images/green-dot-falsk-magic-kort.jpg" alt="Den grønne prikken på baksiden av et falskt Magic-kort under lupe, uten røde prikker" loading="lazy" />
    <figcaption>Falskt kort: ingen røde prikker i det lyse feltet.</figcaption>
  </figure>
</div>

<p>
  Testen har to svakheter. Kraftig slitasje kan gjøre mønsteret vanskelig å se, også på ekte
  kort. Og noen forfalskere lager <em>rebacked</em> kort, der en falsk forside er limt på
  baksiden av et ekte, billig kort – da består kortet green dot-testen. Sjekk derfor alltid
  forsiden med lupe også.
</p>

<h2>6. Tekst, symboler og skrifttype</h2>
<p>
  Forfalskere jobber ofte ut fra skannede bilder, og små detaljer blir feil:
</p>
<ul>
  <li><strong>Skrifttypen</strong> i navn og regeltekst er litt for tykk, tynn eller har feil
    avstand mellom bokstavene.</li>
  <li><strong>Manasymbolene</strong> er feil formet, feil farget eller sitter skjevt.</li>
  <li><strong>Settsymbolet</strong> har feil farge for sjeldenheten, eller er uskarpt.</li>
  <li><strong>Copyright-linjen og samlernummeret</strong> nederst stemmer ikke med settet, eller
    teksten er utydelig. Slå opp kortet på Scryfall og sammenlign linje for linje.</li>
</ul>

<h2>7. Holo-stempelet</h2>
<p>
  Siden Magic 2015 har rares og mythics et lite, blankt holo-stempel nederst på fronten. På ekte
  kort skifter det glans når du vipper kortet, og det er en del av selve trykket. På mange falske
  kort er stempelet matt, flatt, har feil form, eller ser ut som et klistremerke som er lagt
  oppå.
</p>

<h2>8. Overflate, kanter og tykkelse</h2>
<p>
  Kjenn på kortet. Ekte kort har en egen, lett glatt overflate og en bestemt stivhet. Falske kort
  føles ofte for blanke, for matte, for tynne eller for stive. Se også på kantene: ekte kort er
  stanset rent, mens kopier kan ha ujevne kanter eller hjørner med feil radius. Legg kortet oppå
  et ekte kort – stemmer ikke størrelsen helt, har du svaret.
</p>

<h2>9. Vekttesten</h2>
<p>
  Har du en presisjonsvekt som måler hundredels gram, kan du veie kortet. Et ekte kort veier
  rundt 1,7–1,8 gram, og kort fra samme trykkeperiode veier svært likt. Avviker kortet tydelig fra
  et ekte kort av samme type, er det et faresignal. Vei uten lomme, og sammenlign med flere ekte
  kort hvis du kan.
</p>

<h2>Tester vi ikke anbefaler</h2>
<p>
  Du vil se folk anbefale å bøye kortet for å se om det spretter tilbake, eller å rive i et
  hjørne for å se det mørke laget. Begge kan ødelegge et ekte kort, og et bøyd kort mister fort
  mye av verdien – les mer om det i guiden vår om
  <a href="/blogg/mtg-kort-tilstand">tilstand på Magic-kort</a>. Hold deg til testene over, og
  spør noen med erfaring hvis du fortsatt er usikker.
</p>

<h2>Falske kort, proxies og playtest-kort</h2>
<p>
  Ikke alle kopier er laget for å lure noen. Mange spillere bruker proxies – tydelig merkede
  erstatningskort – til uformelle spill eller for å teste et dekk. Det er helt greit så lenge alle
  vet hva det er. Problemet oppstår når slike kort havner i en samling og blir solgt videre som
  ekte, noen ganger uten at selgeren selv vet det. Har du arvet eller kjøpt en samling brukt, er
  det derfor lurt å se gjennom de dyreste kortene før du selger.
</p>

<h2>Slik håndterer vi falske kort</h2>
<p>
  Vi sjekker <strong>alle samlinger og innsendte kort</strong> for forfalskninger før oppgjør.
  De dyreste kortene får ekstra oppmerksomhet, med lupe og sammenligning mot ekte eksemplarer.
  Det gjør vi både for vår egen del og for at kortene vi selger videre skal være til å stole på.
</p>
<p>
  Finner vi falske kort, blir de <strong>destruert</strong>. Vi betaler ikke for dem, og vi
  sender dem ikke tilbake – da ville vi bare bidratt til at de kom ut i omløp igjen. Vi gir deg
  beskjed om hvilke kort det gjelder før oppgjør, slik at du vet hva som er trukket fra.
</p>
<p>
  Vi vet at de aller fleste som sender oss falske kort, ikke vet om det selv. Det er derfor vi
  heller sier fra enn at kortene sendes videre til neste mann.
</p>

<h2>Selg kortene dine trygt</h2>
<p>
  Har du sjekket kortene og vil selge? Slå dem opp i <a href="/buylist">buylisten</a> og se hva du
  får for dem med én gang. Har du mange kort, kan du
  <a href="/csv-opplasting">laste opp en CSV-liste</a> fra ManaBox, Deckbox eller Moxfield. Sitter
  du på en hel samling, tar vi gjerne en titt på den <a href="/selge-samling">under ett</a> – og
  sjekker ektheten for deg på veien.
</p>
    `.trim(),
    faq: [
      {
        question: 'Hvordan ser jeg om et Magic-kort er falskt?',
        answer:
          'Sammenlign kortet med et ekte kort fra samme periode. De mest pålitelige testene hjemme er lystesten (hold kortet foran en lykt – falske kort slipper ofte gjennom mye mer lys) og lupetesten, der ekte kort viser et rosettmønster av små fargeprikker. Se også på fargen på baksiden, skrifttypen, manasymbolene og holo-stempelet.',
      },
      {
        question: 'Hva er green dot-testen?',
        answer:
          'Du ser på den grønne manaprikken på baksiden av kortet gjennom en lupe. På ekte Magic-kort ser du et rosettmønster av blå og gule prikker og fire små røde prikker som danner en «L». Mangler «L»-en, er kortet nesten helt sikkert falskt. Testen avslører ikke rebacked kort, der en falsk forside er limt på en ekte bakside.',
      },
      {
        question: 'Hvilke Magic-kort blir oftest forfalsket?',
        answer:
          'Dyre kort: Reserved List-kort som dual lands og kort fra Alpha, Beta, Unlimited og Revised, dyre staples fra Modern, Legacy og Commander, og foils. Billige kort blir sjelden forfalsket.',
      },
      {
        question: 'Sjekker dere kortene for forfalskninger?',
        answer:
          'Ja. Vi sjekker alle samlinger og innsendte kort for forfalskninger før oppgjør, med ekstra oppmerksomhet på de dyreste kortene.',
      },
      {
        question: 'Hva skjer hvis jeg sender inn et falskt kort?',
        answer:
          'Falske kort blir destruert. Vi betaler ikke for dem og sender dem ikke tilbake, slik at de ikke kommer ut i omløp igjen. Vi gir deg beskjed om hvilke kort det gjelder før oppgjør.',
      },
    ],
  },
  {
    slug: 'mtg-kort-tilstand',
    title: 'Hvilken tilstand er Magic-kortene dine i? NM, EX, GD, LP og PL forklart',
    excerpt:
      'Tilstanden avgjør hva du får betalt for kortene dine. Vi følger Cardmarket sin standard – her er hva NM, EX, GD, LP, PL og PO faktisk betyr, og hvordan du vurderer kortene dine selv.',
    date: '2026-09-14',
    tags: ['MTG', 'tilstand', 'grading', 'selge', 'Cardmarket'],
    content: `
<p>
  Skal du selge Magic-kort, er tilstanden det andre du må ta stilling til etter hvilket kort det
  er. Den avgjør hva du får betalt, og den er den vanligste kilden til misforståelser mellom
  kjøper og selger. Vi følger
  <a href="https://help.cardmarket.com/en/CardCondition" target="_blank" rel="noopener noreferrer">Cardmarket sin standard</a>
  til punkt og prikke, så kortene dine vurderes likt hos oss som på Europas største MTG-marked.
</p>

<h2>Hvorfor tilstanden betyr så mye</h2>
<p>
  Alle prisene du ser i <a href="/buylist">buylisten</a> gjelder kort i Near Mint. Er kortet mer
  slitt, reduseres tilbudet etter tabellen i <a href="/terms#section-7">vilkårene våre</a>. Hvor
  mye det slår ut, avhenger både av tilstanden og av hva kortet er verdt:
</p>
<ul>
  <li>Et kort under 15 € i Excellent gir 80 % av NM-prisen. I Light Played gir det 50 %.</li>
  <li>Et kort over 100 € tåler mer – der gir Excellent fortsatt 90 %.</li>
  <li>Foils er der tilstand slår hardest ut: Excellent gir 75 %, Light Played bare 30 %.</li>
</ul>
<p>
  Med andre ord: på et billig kort betyr én grad fra eller til noen kroner, mens på en dyr foil
  kan det bety flere hundre. Det er verdt å bruke et minutt på å vurdere riktig. Vil du vite mer
  om selve satsene, har vi skrevet om
  <a href="/blogg/hva-betaler-vi-for-magic-kort">hvor mye vi betaler for Magic-kort</a>.
</p>

<h2>Slik vurderer du et kort</h2>
<p>
  Gradering er ingen eksakt vitenskap, og to personer kan lande på hver sin grad for samme kort.
  Men de fleste uenigheter forsvinner hvis du gjør dette:
</p>
<ul>
  <li><strong>Bruk godt lys.</strong> Dagslys fra et vindu er best. Slitasje som er usynlig i
    lampelys, dukker opp med én gang i dagslys.</li>
  <li><strong>Vipp kortet mot lyskilden.</strong> Det er slik du får øye på riper og clouding –
    de er nesten umulige å se rett ovenfra.</li>
  <li><strong>Se på baksiden for seg.</strong> Det er ofte baksiden som avslører et kort som ser
    fint ut fra fronten.</li>
  <li><strong>Sjekk kanter og hjørner</strong> mot en mørk bakgrunn. Hvite prikker syns best da.</li>
  <li><strong>Legg kortet flatt på bordet.</strong> Så ser du om det bøyer seg.</li>
  <li><strong>Ta kortet ut av lommen.</strong> En sleeve skjuler både riper og slitte kanter.</li>
</ul>
<p>
  Er du i tvil mellom to grader, velg den laveste. Det gir en ryddigere handel enn å bli justert
  ned i etterkant.
</p>

<h2>De seks tilstandene</h2>

<h3>Near Mint (NM)</h3>
<p>
  Et NM-kort ser ut som det aldri har vært spilt uten lommer. Små avvik kan aksepteres, men
  kortet viser i praksis ingen slitasje. Kanten kan ha noen få, svært små hvite prikker. I
  dagslys skal overflaten se ren ut – noen få ubetydelige merker går an, men
  <strong>riper diskvalifiserer alltid et kort fra NM</strong>. Det samme gjør synlig clouding.
</p>

<h3>Excellent (EX)</h3>
<p>
  Et EX-kort ser ut som det har vært med på noen få spill uten lommer. Her er det som regel
  tydelig ved første øyekast at kortet ikke er perfekt – men skaden er liten. Typisk noen hvite
  prikker i hjørnene eller langs kanten, og små riper som syns når du ser etter. Er merkene så
  dype at de slår deg med én gang, er kortet dårligere enn EX.
</p>

<h3>Good (GD)</h3>
<p>
  Et GD-kort ser ut som det har vært gjennom en lang turnering uten lommer. Tydelig slitasje hele
  veien rundt, mange hvite prikker på kanter og hjørner, riper på overflaten og gjerne litt skitt.
  Men all skaden stammer fra vanlig spill: ingen vannskade, ingen brekk. Navnet er misvisende –
  et Good-kort ser ikke bra ut.
</p>

<h3>Light Played (LP)</h3>
<p>
  Et LP-kort ser ut som det har vært brukt uten lommer over lang tid. Det kan se ganske stygt ut,
  men to ting må stemme: kortet er klart lovlig å spille i et dekk med lommer, og det er ikke
  tuklet med. Er begge deler oppfylt, er LP riktig grad uansett hvor slitt kortet ser ut.
</p>

<h3>Played (PL)</h3>
<p>
  Så ille som et kort kan bli gjennom vanlig bruk uten lommer. Det er tvilsomt om kortet er
  turneringslovlig selv i lommer. Fortsatt ingen tukling – bare hard bruk.
</p>

<h3>Poor (PO)</h3>
<p>
  Poor er skade som ikke kan ha kommet av vanlig bruk: inket eller svertet kant, skriblerier,
  kraftige brekk, alvorlig vannskade. Kortet er i praksis ødelagt.
  <strong>Vi kjøper ikke kort i Poor</strong>, så disse trenger du ikke sende inn.
</p>

<p>
  Cardmarket har også en Mint-grad (M) over Near Mint, men den bruker vi ikke i buylisten. For
  nyere kort er verdien av et Mint-kort praktisk talt den samme som et NM-kort, og for de virkelig
  gamle kortene der Mint faktisk gir et påslag, handler det uansett om profesjonell gradering.
</p>

<h2>Ting som setter et tak på graden</h2>
<p>Noen enkeltfeil avgjør graden alene, uansett hvor fint resten av kortet er:</p>
<ul>
  <li><strong>Riper på overflaten</strong> – kortet blir Good i beste fall.</li>
  <li><strong>Synlig clouding</strong> – kortet kan aldri bli Near Mint.</li>
  <li><strong>Vannskade</strong> – LP hvis kortet klart er turneringslovlig, PO hvis det klart
    ikke er det, PL et sted imellom.</li>
  <li><strong>Brekk</strong> – er kortet så varig deformert at det neppe er turneringslovlig,
    blir det Played.</li>
  <li><strong>Signatur, GP-stempel, inket eller hvitnet kant</strong> – alltid Poor, selv om
    kortet ellers ser nytt ut.</li>
  <li><strong>Produksjonsfeil</strong> – skjev sentrering eller rar tekst er ikke slitasje og
    påvirker ikke graden.</li>
</ul>

<h2>Foils</h2>
<p>
  De samme reglene gjelder for foils, men de er strengere i praksis. Foil-overflaten er langt mer
  sårbar for riper, og både clouding og brekk på fronten syns mye bedre enn på et vanlig kort. Se
  spesielt etter disse to.
</p>
<p>
  Lett krumming er normalt på foils og senker ikke graden i seg selv. Men er kortet så deformert
  at det neppe går i en turnering, er vi nede på Played.
</p>

<h2>Gamle kort vurderes likt</h2>
<p>
  Et vanlig spørsmål fra folk som selger samlinger: teller det ikke at kortet er fra 1994? Nei –
  alderen gir ingen rabatt på vurderingen. Et 30 år gammelt kort måles etter nøyaktig de samme
  kriteriene som et kort fra siste sett. I pristabellen vår går det faktisk motsatt vei: for
  Alpha, Beta og Unlimited slår tilstanden hardere ut enn ellers, fordi forskjellen i
  annenhåndsverdi mellom en pen og en slitt kopi er så stor. Vi har skrevet mer om
  <a href="/blogg/slik-priser-vi-mtg-kort">hvorfor gamle og nye kort prises ulikt</a>.
</p>

<h2>Hvis vi vurderer kortet annerledes enn deg</h2>
<p>
  Vi går gjennom kortene når de kommer inn. Lander vi på en annen grad enn du oppga, justerer vi
  tilbudet etter tabellen i vilkårene og gir deg beskjed før noe gjøres opp. Prisene i buylisten
  er indikative nettopp fordi den endelige vurderingen skjer når kortet ligger foran oss. Er du
  usikker på et dyrt kort, <a href="/contact">ta kontakt</a> med bilder før du sender – det tar
  fem minutter og sparer begge parter for arbeid.
</p>

<h2>Klar til å selge?</h2>
<p>
  Nå som du vet hvilken tilstand kortene dine er i, kan du slå dem opp i
  <a href="/buylist">buylisten</a> og se hva de gir. Har du mange kort, kan du
  <a href="/csv-opplasting">laste opp en CSV-liste</a> fra ManaBox, Deckbox eller Moxfield og få
  alt priset på sekunder. Sitter du på en hel samling, ser vi heller på den
  <a href="/selge-samling">under ett</a>.
</p>
    `.trim(),
    faq: [
      {
        question: 'Hva er forskjellen på Near Mint og Excellent?',
        answer:
          'Et Near Mint-kort viser i praksis ingen slitasje og ser ut som det aldri har vært spilt uten lommer. På et Excellent-kort ser du ved første øyekast at det er brukt – noen hvite prikker i hjørnene og små riper – men skaden er fortsatt liten.',
      },
      {
        question: 'Gjør riper at kortet ikke lenger er Near Mint?',
        answer:
          'Ja. Riper på overflaten diskvalifiserer et kort fra både Near Mint og Excellent, og gjør at kortet blir Good i beste fall. Riper er spesielt viktig på foils, der overflaten er mer sårbar.',
      },
      {
        question: 'Kjøper dere kort i Poor-tilstand?',
        answer:
          'Nei. Kort i Poor er i praksis ødelagt – inket eller svertet kant, skriblerier, kraftige brekk eller alvorlig vannskade – og disse kjøper vi ikke. Kort helt ned til Played kjøper vi gjerne.',
      },
      {
        question: 'Hva skjer hvis jeg oppgir feil tilstand på et kort?',
        answer:
          'Vi vurderer kortene når de kommer inn. Lander vi på en annen grad enn du oppga, justerer vi tilbudet etter tabellen i vilkårene og gir deg beskjed før oppgjør. Er du i tvil mellom to grader, velg den laveste.',
      },
    ],
  },
  {
    slug: 'slik-priser-vi-mtg-kort',
    title: 'Slik priser vi MTG-kort — hvorfor gamle og nye kort prises ulikt',
    excerpt:
      'Skal du selge Magic-kort og lurer på hvordan vi finner markedsprisen? Vi bruker Cardmarket trendpris på nyere kort, laveste pris i samme tilstand på eldre kort, og eBay, Discord og salgsgrupper på de dyreste.',
    date: '2026-07-28',
    tags: ['MTG', 'selge', 'prising', 'Cardmarket', 'gamle kort'],
    content: `
<p>
  Et spørsmål vi får ofte: hvorfor opererer dere med forskjellige priskilder for forskjellige
  kort? Enkelt forklart er det fordi gamle og nye kort har svært forskjellige egenskaper som
  bestemmer prisen — og ingen enkelt priskilde treffer alt.
</p>

<h2>Kort fortalt: to måter vi priser på</h2>
<p>
  Det viktigste å være klar over er at vi priser på to ulike måter, avhengig av om du selger
  enkeltkort gjennom buylisten eller en hel samling.
</p>
<div class="table-wrap">
  <table>
    <thead>
      <tr>
        <th></th>
        <th>Automatisk buylist</th>
        <th>Manuell vurdering av samling</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>Gjelder</strong></td>
        <td>Enkeltkort, hovedsakelig nyere</td>
        <td>Hele samlinger, eldre kort, dyre kort</td>
      </tr>
      <tr>
        <td><strong>Priskilde</strong></td>
        <td>Cardmarket trendpris</td>
        <td>Laveste pris i samme tilstand — eBay, Discord og salgsgrupper på de dyreste</td>
      </tr>
      <tr>
        <td><strong>Tilstand</strong></td>
        <td>Prisene gjelder NM. Dårligere tilstand trekkes etter tabellen i
          <a href="/terms#section-7">vilkårene våre</a></td>
        <td>Tilstanden ligger allerede i prisen vi sammenligner med — ikke noe trekk oppå</td>
      </tr>
    </tbody>
  </table>
</div>
<p>Resten av denne posten forklarer hvorfor.</p>

<h2>Nyere kort: Cardmarket trendpris</h2>
<p>
  For nyere kort i <strong>EX- eller NM-tilstand</strong> er det enkelt for både kjøper og selger
  å bruke <strong>Cardmarket trendpris</strong>. Trendprisen er en slags gjennomsnittspris basert
  på den siste tidens salg, og for kort som omsettes jevnlig i god stand gir den et treffsikkert
  bilde av markedet.
</p>
<p>
  Cardmarket har blitt standarden for kortprising i Norge, og det er her de fleste kjøpere i
  Europa faktisk handler. Amerikanske priskilder som TCGplayer er vanskelige å bruke som referanse
  i det norske markedet — prisene der ligger ofte høyere enn hva norske kjøpere kan få kortene for
  ved å importere direkte fra Cardmarket, og da er det Cardmarket-prisen som setter taket for hva
  kortene er verdt her hjemme.
</p>
<p>
  Merk at trendprisen er <em>markedsprisen</em>, ikke det vi betaler. Prosentsatsen vår, og
  eventuelt trekk for tilstand, kommer på toppen. Det er derfor et EX-kort i buylisten gir litt
  mindre enn det samme kortet i NM, selv om begge tar utgangspunkt i den samme trendprisen.
  Hvordan vi vurderer tilstand kan du lese om i
  <a href="https://help.cardmarket.com/en/CardCondition" target="_blank" rel="noopener noreferrer">Cardmarkets
  tilstandsguide</a>, som vi følger strengt.
</p>

<h2>Eldre kort og slitte kort: laveste pris i samme tilstand</h2>
<p>
  For eldre kort, kort i dårlig tilstand og lignende er bildet litt mer komplisert.
</p>
<p>
  Er kortene fra sent 90-tall, er det en del omsetning på Cardmarket, slik at det nesten alltid er
  mulig å bruke <strong>den laveste listede prisen for kort i samme tilstand</strong>. Grunnen til
  at vi ikke kan bruke trend på slike kort, er at et kort i dårlig tilstand vil ha en markedspris
  som ligger langt under trend, og et kort i veldig god tilstand vil ha en markedspris som er mye
  høyere enn trend. Trend regnes nemlig ut fra kort i alle tilstander som blir solgt. For eldre
  kort er Cardmarket trendpris derfor en svært dårlig priskilde.
</p>
<p>
  Og siden vi sammenligner med prisen på kort i den tilstanden dine kort faktisk er i, gjør vi
  ikke noe ekstra tilstandstrekk oppå det — tilstanden er allerede priset inn. Tilstandstabellen i
  <a href="/terms#section-7">vilkårene</a> gjelder den automatiske buylisten, hvor alle priser
  oppgis for NM.
</p>

<h2>De dyreste kortene: eBay, Discord og salgsgrupper</h2>
<p>
  For de eldste og mest verdifulle kortene — slik som kort fra <strong>Alpha, Beta og
  Unlimited</strong>, og de tre første utvidelsessettene <strong>Arabian Nights, Antiquities og
  Legends</strong> — kan Cardmarket være en dårlig priskilde i seg selv.
</p>
<p>
  Årsaken er likviditet: jo dyrere et kort er, desto lavere likviditet har det. Det er jo svært få
  som bruker over 100 000 kroner på et samlekort, som for eksempel en <strong>Black Lotus</strong>.
  Med så få salg å basere seg på blir enhver listepris fort tilfeldig.
</p>
<p>
  For slike kort bruker vi i Tryllepapp alternative priskilder som <strong>eBay, Discord og
  Facebook-grupper</strong>, hvor vi kan se hva kortene faktisk omsettes for. Alltid med 100 %
  åpenhet om hvordan vi har kommet fram til markedsprisen på et kort. Det samme kan gjelde andre
  kort med lav likviditet, som serialiserte kort eller svært sjeldne nye foils.
</p>
<p>
  Flere av kortene vi betaler vår høyeste sats for hører hjemme i akkurat denne kategorien — se
  <a href="/selge-samling">80 %-listen på samlingssiden</a>.
</p>

<h2>Hva du kan forvente i prosent</h2>
<p>
  Priskilden avgjør <em>markedsprisen</em>. Hva du får utbetalt, er en prosent av den:
</p>
<ul>
  <li><strong>Samlinger</strong> — 60–75 % av markedspris for enkeltkort med verdi over 2 €, og
    <strong>opptil 80 %</strong> for utvalgte ettertraktede kort.
    <a href="/selge-samling">Se hele oversikten over satser</a>.</li>
  <li><strong>Buylisten</strong> — 60 % av trendpris som grunnsats, 70 % for staples, og 10 %
    ekstra om du velger butikkreditt.
    <a href="/blogg/hva-betaler-vi-for-magic-kort">Les mer om satsene i buylisten</a>.</li>
</ul>
<p>
  Variasjonen skyldes at noen kort får en høyere prosent (f.eks. svært etterspurte staples vi
  trenger raskt), mens andre får lavere fordi de er vanskeligere å omsette — mer om det under.
</p>

<h2>Unntak fra standardprosenten</h2>
<p>
  Det finnes mange tilfeller der vi avviker fra standarden over. Vi vurderer alltid følgende
  faktorer:
</p>
<ul>
  <li><strong>Likviditet</strong> — hvor raskt selger kortet seg igjen?</li>
  <li><strong>Arbeidsmengde</strong> — krever kortet ekstra tid å selge videre?</li>
  <li><strong>Risiko</strong> — hvor lenge kan det ligge i inventaret?</li>
  <li><strong>Nåværende beholdning</strong> — har vi allerede mange eksemplarer på lager?</li>
</ul>
<p>
  Kort som typisk gir lavere enn standardprosent:
</p>
<ul>
  <li><strong>Foil-kort fra før 2012</strong> — eldre foils har begrenset etterspørsel og kan
    ligge lenge. Vi trenger ekstra insentiv for å ta disse inn i inventaret.</li>
  <li><strong>Signerte kort</strong> — signaturer øker ikke nødvendigvis verdien for alle kjøpere,
    og markedet for dem er smalt.</li>
  <li><strong>Ikke-engelske kort</strong> — det er i all hovedsak engelskspråklige kort som lar
    seg omsette i Norge. Kort på andre språk kan være svært vanskelige å selge videre, og vi må
    prise deretter.</li>
</ul>

<h2>Klar til å selge?</h2>
<p>
  Håper dette var oppklarende rundt hvordan vi finner markedsprisen. Har du spørsmål, er det bare
  å <a href="/contact">ta kontakt</a>.
</p>
<p>
  Sjekk <a href="/buylist">buylisten vår</a> for å se hva vi betaler for akkurat dine kort —
  prisene oppdateres jevnlig. Har du mange kort? Bruk
  <a href="/csv-opplasting">CSV-opplasting</a> for å legge til en hel liste på én gang.
  Vil du selge en hel samling? Les mer om å <a href="/selge-samling">selge MTG-kort og samlinger</a>.
</p>
    `.trim(),
    faq: [
      {
        question: 'Hvorfor bruker dere ikke Cardmarket trendpris på gamle kort?',
        answer:
          'Trendprisen regnes ut fra salg av kort i alle tilstander. Et gammelt kort i dårlig stand har derfor en reell markedspris langt under trend, og et i veldig god stand langt over. På eldre kort bruker vi i stedet laveste listede pris for kort i samme tilstand, som treffer markedet mye bedre.',
      },
      {
        question: 'Hvordan priser dere kort i dårlig tilstand?',
        answer:
          'Vi sammenligner med laveste pris for kort i den samme tilstanden på Cardmarket. Da er tilstanden allerede priset inn, og vi gjør ikke noe ekstra tilstandstrekk oppå. I den automatiske buylisten fungerer det motsatt: der oppgis alle priser for NM, og dårligere tilstand trekkes etter tabellen i vilkårene våre.',
      },
      {
        question: 'Hvordan finner dere prisen på svært dyre kort som Black Lotus?',
        answer:
          'Jo dyrere et kort er, desto lavere likviditet har det, og desto færre salg finnes det å basere en pris på. For de dyreste kortene bruker vi derfor eBay, Discord og Facebook-grupper hvor vi ser hva kortene faktisk omsettes for — alltid med full åpenhet om hvordan vi kom fram til prisen.',
      },
    ],
  },
  {
    slug: 'hva-betaler-vi-for-magic-kort',
    title: 'Så mye betaler vi for Magic-kort – satsene i buylisten forklart',
    excerpt:
      'Hvor mye får du når du selger Magic-kort til buylisten vår? Vi går gjennom satsene – fra grunnsatsen på 60 % til staplene som får 70 %, og hvorfor etterspørsel betyr mer enn pris.',
    date: '2026-07-17',
    tags: ['MTG', 'buylist', 'satser', 'selge', 'prising'],
    content: `
<p>
  Når du selger Magic-kort gjennom <a href="/buylist">buylisten vår</a>, beregnes prisen som en
  prosent av trendprisen på Cardmarket. Prosenten er ikke lik for alle kort, og her går vi
  gjennom hvordan satsene fungerer – og hvilke kort som får de høyeste.
</p>

<h2>Grunnsatsen: 60 % av Cardmarket trendpris</h2>
<p>
  Utgangspunktet for de fleste kort i buylisten er <strong>60 % av trendprisen på
  Cardmarket</strong>, regnet om til norske kroner etter dagens kurs. Dette gjelder kort med en
  markedsverdi på over 3 € i buylisten. Prisene hentes automatisk og følger markedet, så tilbudet
  du ser i buylisten i dag kan være et annet enn i forrige uke.
</p>
<p>
  Merk at satsene i denne posten gjelder <strong>den automatiske buylisten</strong>. Selger du en
  hel samling, vurderer vi manuelt, med en annen priskilde og en annen terskel — les om
  <a href="/blogg/slik-priser-vi-mtg-kort">hvorfor gamle og nye kort prises ulikt</a>.
</p>

<h2>Staples får 70 %</h2>
<p>
  Kort vi vet vi får solgt videre raskt, betaler vi mer for. Staples fra ulike formater – kort
  som ser mye spill i Commander, Modern og andre formater – får <strong>70 % av
  trendpris</strong>.
</p>
<p>
  Her er det verdt å merke seg at vi ikke bare ser på hva kortet koster. Etterspørselen betyr
  vel så mye. Enkelte kort som bare er verdt en euro, gir vi likevel 70 % for – rett og slett
  fordi de spilles i så mange dekk at de forsvinner ut like fort som de kommer inn. Slike kort
  kjøper vi også gjerne mange eksemplarer av om gangen.
</p>

<h2>Eldre kort</h2>
<p>
  Gamle kort er vi alltid på jakt etter, og flere av dem har egne satser over grunnsatsen:
</p>
<ul>
  <li><strong>Kort fra før 2012 verdt over 50 €</strong> – 67 % av trendpris.</li>
  <li><strong>Premodern-staples</strong> – 65 % av trendpris.</li>
</ul>
<p>
  Lurer du på om de gamle kortene dine er noe verdt? Vi har skrevet mer om
  <a href="/blogg/er-magic-kort-verdt">hvilke Magic-kort som er verdifulle</a>.
</p>

<h2>10 % ekstra i butikkreditt</h2>
<p>
  Velger du butikkreditt i stedet for utbetaling til konto, legger vi <strong>10 % på hele
  beløpet</strong>. Et kort som gir 70 % av trendpris, gir dermed i praksis 77 % om du heller
  vil handle for pengene hos oss.
</p>

<h2>Tilstanden på kortet betyr mye</h2>
<p>
  Satsene over gjelder kort i Near Mint. Er kortet mer slitt, reduseres prisen etter tabellen i
  <a href="/terms#section-7">vilkårene våre</a>. Dyrere kort tåler litt mer – et kort verdt
  noen hundrelapper i Excellent gir fortsatt 90 % av NM-prisen – mens foils er der tilstand
  slår hardest ut.
</p>

<h2>Kort vi ikke kjøper via buylisten</h2>
<p>Noen kort finner du ikke i buylisten, selv om vi gjerne kjøper dem:</p>
<ul>
  <li><strong>Kort verdt over 300 €</strong> – disse tar vi ikke automatisk. Send oss en
    oversikt på e-post, så gir vi et eget tilbud. <a href="/contact">Ta kontakt her</a>.</li>
  <li><strong>Foils fra før 2012</strong> – vanskelige å omsette, så disse vurderer vi manuelt.</li>
  <li><strong>Helt nye kort</strong> – vi venter til prisen har fått satt seg etter at et sett
    slippes.</li>
  <li><strong>Billige kort uten særlig etterspørsel</strong> – kort under noen få euro som ikke
    er staples. Har du mye av dette, kan det passe bedre som del av et
    <a href="/selge-samling">samlingssalg av Magic-kort</a>.</li>
</ul>

<h2>Sjekk hva kortene dine gir</h2>
<p>
  Den enkleste måten å se satsene i praksis er å slå opp kortene dine i
  <a href="/buylist">buylisten</a>. Har du mange kort, kan du
  <a href="/csv-opplasting">laste opp en CSV-liste</a> fra Manabox, Deckbox eller Moxfield og få
  alt priset på sekunder. Er du ny til konseptet, kan du lese om
  <a href="/blogg/hva-er-en-mtg-buylist">hva en MTG buylist er</a>.
</p>
    `.trim(),
    faq: [
      {
        question: 'Hvor mye får jeg for Magic-kortene mine i buylisten?',
        answer:
          'De fleste kort gir 60 % av Cardmarket trendpris, mens staples fra ulike formater gir 70 %. Eldre kort fra før 2012 verdt over 50 € gir 67 %. Velger du butikkreditt, får du 10 % ekstra på hele beløpet.',
      },
      {
        question: 'Får jeg mer betalt i butikkreditt enn i kontanter?',
        answer:
          'Ja, butikkreditt gir 10 % ekstra. Et kort som gir 70 % av trendpris i kontanter, gir 77 % i butikkreditt.',
      },
      {
        question: 'Hvorfor finner jeg ikke kortet mitt i buylisten?',
        answer:
          'Kort verdt over 300 €, foils fra før 2012 og helt nye kort kjøpes ikke automatisk via buylisten. Dyre kort kjøper vi gjerne likevel – send oss en e-post, så får du et eget tilbud.',
      },
    ],
  },
  {
    slug: 'selge-mtg-kort',
    title: 'Hvor kan du selge MTG-kort i Norge? Alle alternativene sammenlignet',
    excerpt:
      'Finn, Facebook, Cardmarket, din LGS eller en buylist? Vi sammenligner alternativene for å selge Magic-kort i Norge – og hva du kan forvente å få betalt.',
    date: '2026-06-21',
    tags: ['MTG', 'selge', 'buylist', 'tips'],
    content: `
<p>
  Å selge MTG-kort i Norge foregår i hovedsak gjennom noen få kanaler. Alle har sine fordeler
  og ulemper – her sammenligner vi hvor du kan selge Magic-kort og hva du kan forvente å få
  betalt. Vil du heller gå rett på sak, kan du
  <a href="/selge-samling">selge MTG-kort til Tryllepapp</a> – enkeltkort eller hele samlingen.
</p>

<h2>Hvor kan du selge MTG-kort i Norge?</h2>
<p>De vanligste kanalene for å selge Magic-kort i Norge er:</p>
<ul>
  <li>
    <strong>Finn.no</strong> – stor norsk markedsplass. Fint for dyre enkeltkort og hele samlinger,
    men du må selv håndtere annonser, spørsmål, prutting og forsendelse.
  </li>
  <li>
    <strong>Facebook-grupper som «MTG Trading Norge»</strong> – her er kjøperne spillere som vet hva
    de ser etter. Ofte gode priser for populære kort, men det krever tid, og du må vurdere hvem du
    handler med.
  </li>
  <li>
    <strong>Cardmarket</strong> – Europas største markedsplass for Magic-kort, og der markedsprisene
    settes. Stort publikum, men gebyrer, porto til utlandet og mye pakking gjør det lite lønnsomt for
    billige kort.
  </li>
  <li>
    <strong>Din LGS (local game store) eller egen playgroup</strong> – raskt og enkelt, og du kan ofte
    få butikkreditt. Prisen avhenger av hva butikken trenger på lager.
  </li>
  <li>
    <strong>En buylist eller dedikert videreselger</strong> – du får en fast pris med en gang og slipper
    å selge kort for kort. Du får mindre enn ved å selge selv, men sparer mye tid, og du blir kvitt
    også kortene som er vanskelige å selge.
  </li>
</ul>

<h2>Selge enkeltkort for høyest mulig pris</h2>
<p>
  Dersom målet er å oppnå høyest mulig pris for kortene dine, er den eneste muligheten å selge
  dem ett og ett. Det er tidkrevende, men med denne metoden kan du forvente et sted mellom
  80–100 % av prisene på Cardmarket, avhengig av hvor god tid du har til å selge.
</p>
<p>
  Ulempen er at du som regel får solgt de mest ettertraktede kortene dine, mens du blir
  sittende igjen med mye av resten. Det er vanskeligere å bli kvitt dersom du ikke har et svært
  bredt utvalg.
</p>

<h2>Noen kort er vanskelige å selge i Norge</h2>
<p>
  Enkelte kort kan være svært vanskelige å selge, spesielt i Norge. Det kommer av at kjøpere av
  Magic-kort i all hovedsak er spillere, og for enkelte kort finnes det ikke nok spillere av
  visse formater her i landet. I disse tilfellene må du selge kortene utenlands, noe som
  eksponerer deg for større risiko og er mer komplisert med tanke på toll, betalingsmåter og
  forsendelse.
</p>

<h2>Selge til en LGS – hva bør du forvente?</h2>
<p>
  Velger du å selge til en LGS (local game store), er det viktig at du vet hva du kan forvente å
  få for kortene dine ved et samlet salg. Avhengig av hvilke kort du har, hvor sjeldne de er og
  hvor enkle de er å selge igjen, bør du kunne forvente mellom 60–75 % av Cardmarket-priser. Vil
  du vite mer om hvordan slike priser settes, kan du lese om
  <a href="/blogg/slik-priser-vi-mtg-kort">hvordan vi priser MTG-kort</a>.
</p>
<p>
  Får du et tilbud som er lavere enn dette hos din LGS, er det bare å
  <a href="/contact">kontakte oss for et tilbud</a>.
</p>

<h2>Selg enkeltkort eller hele samlingen til oss</h2>
<p>
  Vi tilbyr både å kjøpe hele samlinger og å gjøre hele jobben med å prise alt du har – les mer om
  hvordan du kan <a href="/selge-samling">selge Magic-kort og samlinger til oss</a>. Vi har
  også en <a href="/buylist">stående kjøpsliste for enkeltkort</a>, der du kan søke opp og velge
  ut kortene du vil selge. Vil du vite mer om hvordan det fungerer, kan du lese om
  <a href="/blogg/hva-er-en-mtg-buylist">hva en MTG buylist er</a>.
</p>
<p>
  Har du en liste i Manabox-appen, på deckbox.com eller moxfield.com, kan du
  <a href="/csv-opplasting">laste den opp her</a>. Det er den enkleste måten å få en indikasjon
  på hva vi kan gi for kortene dine.
</p>
<p>
  Er tilstanden dårligere enn NM, blir prisene våre redusert etter
  <a href="/terms#section-7">denne tabellen</a>. Usikker på om de gamle kortene dine er verdt
  noe? Les <a href="/blogg/er-magic-kort-verdt">om hvilke Magic-kort som er verdifulle</a>.
</p>
    `.trim(),
  },
  {
    slug: 'er-magic-kort-verdt',
    title: 'Er mine gamle Magic-kort noe verdt?',
    excerpt:
      'Lurer du på om de gamle Magic-kortene dine er verdt noe? Vi viser hvilke kort som er verdifulle – og gir deg gratis verdivurdering og tilbud.',
    date: '2026-06-11',
    tags: ['MTG', 'verdivurdering', 'samling', 'tips'],
    content: `
<p>
  Det kommer an på. De aller fleste Magic-kort er verdt svært lite, men noen få kan være verdt mye
  – fra noen hundrelapper til titusenvis av kroner. Her viser vi hvilke kort du bør se etter, og
  hvordan du finner ut hva samlingen din er verdt.
</p>

<h2>Hvorfor er de fleste Magic-kort verdt lite?</h2>
<p>
  De aller fleste Magic-kort er verdt veldig, veldig lite – så lite at de nærmest kan selges til
  kilopris. Grunnen er at Magic: The Gathering i hovedsak er et kortspill, og sekundært et
  samleobjekt. Det gjør at de kortene som er gode nok til å spille blir mye dyrere enn de som ikke
  er det. De sjeldne Magic-kortene som faktisk er verdt penger, gjemmer seg derfor ofte i bunken –
  så det er verdt å sjekke før du gir bort samlingen.
</p>

<h2>Verdifulle kort fra 1993–1999</h2>
<p>
  Går vi helt tilbake til begynnelsen av Magic i 1993, kan enkelte kort være verdt ekstremt mye.
  De mest kjente er:
</p>
<ul>
  <li><strong>Power 9</strong> – de ni mest ettertraktede kortene fra de aller første settene.</li>
  <li><strong>Dobbeltland (dual lands)</strong> – de originale tofargede landene.</li>
</ul>

<figure>
  <img src="/blog-images/power-9-magic-kort.jpg" alt="Power 9 – de mest verdifulle Magic-kortene fra 1993" loading="lazy" />
  <figcaption>Power 9 – de mest verdifulle Magic-kortene fra 1993.</figcaption>
</figure>

<figure>
  <img src="/blog-images/dual-lands-magic-kort.jpg" alt="De ti originale dual lands fra de første Magic-settene" loading="lazy" />
  <figcaption>De ti originale dobbeltlandene (dual lands).</figcaption>
</figure>

<p>
  Fra den gylne æraen på slutten av 90-tallet er det også flere kort å se etter:
</p>
<ul>
  <li>Gaea's Cradle</li>
  <li>Mox Diamond</li>
  <li>Survival of the Fittest</li>
  <li>City of Traitors</li>
  <li>Serra's Sanctum</li>
  <li>Intuition</li>
  <li>Phyrexian Dreadnought</li>
  <li>Lion's Eye Diamond</li>
</ul>

<figure>
  <img src="/blog-images/gylne-aera-90-talls-kort-magic-kort.jpg" alt="Verdifulle Magic-kort fra slutten av 90-tallet i originalutgave" loading="lazy" />
  <figcaption>Verdifulle kort fra slutten av 90-tallet – her i originalutgavene.</figcaption>
</figure>

<p>
  Det finnes flere kort fra denne æraen som er verdt penger, men det kan være mye å sette seg inn i,
  og kortene kan tidvis være vanskelige å omsette. Ønsker du heller et tilbud med en gang, kan du
  lese mer om å <a href="/selge-samling">selge Magic-samlingen din</a>.
</p>

<h2>Verdifulle kort fra 2000–2010</h2>
<p>
  Har du kort fra perioden 2000–2010, er dette noen av de verdifulle kortene du bør se etter:
</p>
<ul>
  <li><strong>Chrome Mox</strong></li>
  <li><strong>Fetchlandene fra Onslaught-serien</strong> (for eksempel Polluted Delta og Flooded Strand).</li>
</ul>

<figure class="card-single">
  <img src="/blog-images/chrome-mox-magic-kort.jpg" alt="Chrome Mox – et verdifullt Magic-kort fra 2000-tallet" loading="lazy" />
  <figcaption>Chrome Mox – et verdifullt Magic-kort fra 2000-tallet.</figcaption>
</figure>

<figure>
  <img src="/blog-images/onslaught-fetchland-magic-kort.jpg" alt="De fem fetchlandene fra Onslaught-serien" loading="lazy" />
  <figcaption>Fetchlandene fra Onslaught-serien.</figcaption>
</figure>

<h2>Foil-kort — slik kjenner du dem igjen</h2>
<p>
  Fra 1999 introduserte Magic kort i foil. Disse kortene glinser, og i de tidligere utgavene er det
  en skytestjerne nederst til venstre på kortet.
</p>
<p>
  Foil-kort er ofte verdt flere ganger mer enn en vanlig utgave av samme kort. Men her er tilstanden
  på kortet ekstremt utslagsgivende – både for verdivurderingen og for hvor enkelt det er å omsette.
  Vi gir ofte en litt lavere prosentsats for foil-kort selv om de er dyrere, rett og slett fordi de
  er vanskeligere å selge videre.
</p>

<!-- TODO: foil-bilde kommer — legg inn ekte foto av et foil-kort som viser skytestjernen.
<figure>
  <img src="/blog-images/grim-monolith-foil-skytestjerne.jpg" alt="Foil-utgave av Grim Monolith med skytestjerne nederst til venstre" loading="lazy" />
  <figcaption>Foil-utgave av Grim Monolith med skytestjerne nederst til venstre.</figcaption>
</figure>
-->

<h2>Hvordan finne ut hva kortene dine er verdt</h2>
<p>
  Vil du sjekke selv, kan du begynne med å se på settsymbolet og sjeldenhetsgraden, og deretter slå
  opp kortene på Cardmarket for å se hva de omsettes for. Det kan likevel være tidkrevende å verdsette
  en hel samling på egen hånd.
</p>
<p>
  Den enkleste måten er å la oss ta verdivurderingen for deg. Buylisten og CSV-opplastingen
  håndterer de rimeligere kortene – alt opp til 300 euro. Har du noen få kort, kan du
  <a href="/buylist">legge dem inn i buylisten vår</a>. Har du en lang liste, kan du
  <a href="/csv-opplasting">laste opp en kortliste (CSV)</a> og få alt priset på sekunder. Da slipper
  du å gjette på hva det koster å selge gamle Magic-kort – du ser tilbudet direkte.
</p>
<p>
  Har du dyrere kort som er verdt over 300 euro, tar vi dem ikke via buylisten. Send oss
  en oversikt på e-post, så gir vi deg et eget tilbud på dem. <a href="/contact">Ta kontakt med
  oss her</a>.
</p>
    `.trim(),
    faq: [
      {
        question: 'Hvordan vet jeg om Magic-kortene mine er verdt noe?',
        answer:
          'Se etter kort fra de tidlige settene (1993–1999), foil-kort med skytestjerne nederst til venstre, og kjente spillkort. Du kan sjekke prisene på Cardmarket, eller sende oss en liste eller bilder for en gratis verdivurdering.',
      },
      {
        question: 'Hvor kan jeg selge gamle Magic-kort?',
        answer:
          'Du kan selge gamle Magic-kort til oss i Tryllepapp – en stående MTG buylist i Norge. Vi kjøper både enkeltkort og hele samlinger, og gir rask utbetaling eller 10 % ekstra i butikkreditt.',
      },
      {
        question: 'Er foil-kort mer verdt?',
        answer:
          'Som regel ja – foil-kort er ofte verdt flere ganger mer enn en vanlig utgave. Men tilstanden er ekstra viktig for foil, både for verdien og for hvor enkelt kortet er å selge.',
      },
    ],
  },
  {
    slug: 'hva-er-en-mtg-buylist',
    title: 'Hva er en MTG buylist, og hvordan fungerer det?',
    excerpt:
      'En buylist er en oversikt over Magic: The Gathering-kort en forhandler ønsker å kjøpe. Her forklarer vi hvordan det fungerer og hvorfor det er den enkleste måten å selge MTG-kort i Norge.',
    date: '2026-06-06',
    tags: ['MTG', 'buylist', 'tips'],
    content: `
<h2>Hva er en buylist?</h2>
<p>
  En buylist er rett og slett en liste over kort som en forhandler aktivt ønsker å kjøpe fra spillere.
  I stedet for at du som selger må finne kjøpere selv på Facebook-grupper eller Finn.no, kan du bare
  sjekke buylist-en, legge kortene dine i en handlekurv og sende dem inn — akkurat som å handle på nett,
  bare baklengs.
</p>

<h2>Hvorfor selge via en buylist?</h2>
<p>
  Det finnes flere grunner til at mange foretrekker å selge via buylist fremfor å selge direkte til andre spillere:
</p>
<ul>
  <li><strong>Raskt og enkelt</strong> — ingen ventetid, ingen forhandlinger, ingen no-shows.</li>
  <li><strong>Fast pris</strong> — du vet nøyaktig hva du får betalt før du sender kortene.</li>
  <li><strong>Trygt</strong> — du handler med en etablert aktør, ikke en ukjent privatperson.</li>
  <li><strong>Rask utbetaling</strong> — pengene kommer til konto etter at kortene er mottatt og kontrollert.</li>
</ul>

<h2>Hvordan fungerer Tryllepapp sin buylist?</h2>
<p>
  På Tryllepapp kan du søke opp enkeltkortet du vil selge, velge tilstand (NM, EX, GD osv.) og legge det i
  handlekurven. Når du er ferdig, sender du kortene til oss og vi betaler ut til din konto.
  Vi tilbyr også 10% ekstra dersom du velger butikkreditt fremfor kontantutbetaling.
</p>
<p>
  Lurer du på hva vi betaler for akkurat dine kort?
  <a href="/buylist">Sjekk buylist-en vår her</a> — prisene oppdateres jevnlig.
</p>

<h2>Hva betyr de ulike kortstandene?</h2>
<p>
  MTG-kort graderes etter tilstand, og prisen du får avhenger av hvilken tilstand kortet er i:
</p>
<ul>
  <li><strong>NM (Near Mint)</strong> — Så godt som nytt. Ingen synlige merker.</li>
  <li><strong>EX (Excellent)</strong> — Minimale slitasjetegn, ikke synlig under vanlig spilling.</li>
  <li><strong>GD (Good)</strong> — Noe slitasje, men fullt brukbart i spill.</li>
  <li><strong>LP (Lightly Played)</strong> — Tydelig brukt, men ikke skadet.</li>
  <li><strong>PL (Played)</strong> — Tydelig slitasje, men kortets tekst og bilde er leselig.</li>
  <li><strong>PO (Poor)</strong> — Mye slitasje, knapt turnamentverdig.</li>
</ul>
<p>
  Vår buylist viser pris for NM-kort som standard. Er kortene dine i dårligere stand, juster tilstanden
  i handlekurven for å se riktig pris.
</p>

<h2>Klar til å selge?</h2>
<p>
  Gå til <a href="/buylist">buylist-en</a>, finn kortene dine og legg dem i handlekurven.
  Har du mange kort? Bruk <a href="/csv-opplasting">CSV-opplasting</a> for å legge til en hel liste på én gang.
  Har du en hel samling du vil bli kvitt? Les mer om å <a href="/selge-samling">selge Magic-kort og samlinger</a>.
</p>
    `.trim(),
  },
];

export default blogPosts;
